import { useAppSelector } from "@app/hooks";
import {
  selectCurrentProperty,
  selectCurrentPropertyId,
  selectCurrentSurvey,
  selectMediaFiles,
  selectProperties,
  selectPropertyMedia,
  selectSurveyConfig,
  selectVisibleProperties,
} from "@survey/surveySelectors";

/**
 * Collection of focused hooks using surveySelectors
 * Promotes consistent store-first architecture across the application
 */

// Basic survey data hook
export const useSurvey = () => useAppSelector(selectCurrentSurvey);

// Survey configuration hook
export const useSurveyConfig = () => useAppSelector(selectSurveyConfig);

// Properties hooks
export const useProperties = () => useAppSelector(selectProperties);
export const useVisiblePropertiesOnly = () =>
  useAppSelector(selectVisibleProperties);
export const useCurrentProperty = () => useAppSelector(selectCurrentProperty);
export const useCurrentPropertyId = () =>
  useAppSelector(selectCurrentPropertyId);
export const useIsPropertiesDrawerOpen = () =>
  useAppSelector((state) => state.survey.isPropertiesDrawerOpen);
export const useCurrentPoiId = () =>
  useAppSelector((state) => state.survey.currentPoiId);
export const useIsPOIsDrawerOpen = () =>
  useAppSelector((state) => state.survey.isPOIsDrawerOpen);

// Media hooks
export const useMediaFiles = () => useAppSelector(selectMediaFiles);
export const usePropertyMedia = (propertyId: string) =>
  useAppSelector(selectPropertyMedia(propertyId));

// Combined hook for property + media (common pattern)
export const usePropertyWithMedia = (propertyId: string) => {
  const survey = useSurvey();
  const properties = useProperties();
  const propertyMedia = usePropertyMedia(propertyId);

  const property = properties.find((p) => p.propertyId === propertyId);

  return {
    survey,
    property,
    propertyMedia,
  };
};

// Bulk properties with media (for preview components)
export const usePropertiesWithMedia = () => {
  const survey = useSurvey();
  const visibleProperties = useVisiblePropertiesOnly();
  const mediaFiles = useMediaFiles();

  return {
    survey,
    visibleProperties,
    mediaFiles,
  };
};
